import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Copy, ExternalLink, Ban, Check, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = { none: "bis 250 (inkl.)", 500: "bis 500 (+29)", 1000: "bis 1'000 (+59)", unlimited: "unbegrenzt (+99)" };
const TIER_PRICE = { none: 0, 500: 29, 1000: 59, unlimited: 99 };
const TIER_PRICE_Y = { none: 0, 500: 24.65, 1000: 50.15, unlimited: 84.15 };
const STATUS_LABEL = { trialing: "Testphase", active: "Aktiv", past_due: "Überfällig", canceled: "Gekündigt", unpaid: "Unbezahlt", incomplete: "Unvollständig", pending: "Offen", expired: "Abgelaufen", failed: "Fehlgeschlagen" };

export const BillingTab = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ boutique: "", email: "", billing: "monthly", extra_stores: 0, items_tier: "none" });
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const headers = { "X-Admin-Key": adminKey };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/billing`, { headers });
      setData(r.data);
    } catch {
      setError("Abo-Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [adminKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const shownTotal = form.billing === "monthly"
    ? 149 + form.extra_stores * 99 + TIER_PRICE[form.items_tier]
    : Math.round((127 + form.extra_stores * 84.15 + TIER_PRICE_Y[form.items_tier]) * 100) / 100;

  const createLink = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    setLink(null);
    try {
      const r = await axios.post(`${API}/billing/checkout`, { ...form, origin_url: window.location.origin }, { headers });
      setLink(r.data.checkout_url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Checkout-Link konnte nicht erstellt werden.");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const cancelSub = async (subId) => {
    if (!window.confirm("Abo zum Periodenende kündigen?")) return;
    try {
      await axios.post(`${API}/admin/billing/cancel`, { subscription_id: subId }, { headers });
      load();
    } catch {
      setError("Kündigung fehlgeschlagen.");
    }
  };

  const deleteRow = async (sessionId) => {
    if (!window.confirm("Diesen Eintrag ohne Abo endgültig löschen?")) return;
    try {
      await axios.delete(`${API}/admin/billing/${sessionId}`, { headers });
      setData((d) => ({ ...d, count: d.count - 1, subscriptions: d.subscriptions.filter((x) => x.session_id !== sessionId) }));
    } catch {
      setError("Eintrag konnte nicht gelöscht werden.");
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH", { dateStyle: "medium" }) : "–");
  const input = "w-full rounded-full border bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--camel)]";

  return (
    <div className="mt-7 space-y-7" data-testid="admin-billing-panel">
      <form onSubmit={createLink} className="rounded-2xl border p-6" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="billing-create-form">
        <h2 className="font-serif-display text-xl font-semibold">Abo-Link für eine Boutique erstellen</h2>
        <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>
          Link an die Boutique senden – sie hinterlegt ihr Zahlungsmittel bei Stripe, die ersten 30 Tage sind kostenlos, danach bucht das Abo automatisch ab.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <input data-testid="billing-boutique-input" required placeholder="Boutique-Name" value={form.boutique} onChange={(e) => setForm({ ...form, boutique: e.target.value })} className={input} style={{ borderColor: "var(--line)" }} />
          <input data-testid="billing-email-input" required type="email" placeholder="E-Mail der Boutique" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={{ borderColor: "var(--line)" }} />
          <select data-testid="billing-interval-select" value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })} className={input} style={{ borderColor: "var(--line)" }}>
            <option value="monthly">Monatlich</option>
            <option value="yearly">Jährlich (−15 %)</option>
          </select>
          <select data-testid="billing-stores-select" value={form.extra_stores} onChange={(e) => setForm({ ...form, extra_stores: Number(e.target.value) })} className={input} style={{ borderColor: "var(--line)" }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} Zusatz-Filialen</option>)}
          </select>
          <select data-testid="billing-items-select" value={form.items_tier} onChange={(e) => setForm({ ...form, items_tier: e.target.value })} className={input} style={{ borderColor: "var(--line)" }}>
            {Object.entries(TIER_LABEL).map(([k, v]) => <option key={k} value={k}>Artikel {v}</option>)}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button data-testid="billing-create-btn" type="submit" disabled={creating} className="pill-btn rounded-full px-6 py-2.5 text-sm font-medium text-white disabled:opacity-60" style={{ background: "var(--camel)" }}>
            {creating ? "Erstelle …" : "Checkout-Link erstellen"}
          </button>
          <span className="text-sm" data-testid="billing-total-preview">
            <strong>CHF {shownTotal.toLocaleString("de-CH")}</strong>/Monat {form.billing === "yearly" && "(jährliche Zahlung)"} · 30 Tage gratis
          </span>
        </div>
        {link && (
          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border bg-white px-4 py-3" style={{ borderColor: "var(--line)" }} data-testid="billing-link-result">
            <span className="min-w-0 flex-1 truncate text-xs" style={{ color: "var(--muted-warm)" }}>{link}</span>
            <button type="button" data-testid="billing-copy-btn" onClick={copyLink} className="flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-medium" style={{ borderColor: "var(--gold)", color: "var(--camel)" }}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Kopiert" : "Kopieren"}
            </button>
            <a href={link} target="_blank" rel="noopener noreferrer" data-testid="billing-open-btn" className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium text-white" style={{ background: "var(--camel)" }}>
              <ExternalLink size={13} /> Öffnen
            </a>
          </div>
        )}
        {error && <p className="mt-3 text-sm" style={{ color: "var(--rose)" }} data-testid="billing-error">{error}</p>}
      </form>

      <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid="billing-table">
        <table className="w-full text-left text-sm">
          <thead style={{ background: "var(--surface-secondary)" }}>
            <tr>
              {["Datum", "Boutique", "E-Mail", "Paket", "CHF/Mt.", "Status", "Verlängert am", "Rechnung", ""].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={9} className="px-4 py-8 text-center"><Loader2 size={20} className="mx-auto animate-spin" style={{ color: "var(--camel)" }} /></td></tr>
            )}
            {data && data.subscriptions.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center" style={{ color: "var(--muted-warm)" }}>Noch keine Abos.</td></tr>
            )}
            {data && data.subscriptions.map((s) => (
              <tr key={s.session_id} className="border-t align-top" style={{ borderColor: "var(--line)" }} data-testid={`billing-row-${s.session_id}`}>
                <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(s.created_at)}</td>
                <td className="px-4 py-3 font-medium">{s.boutique}</td>
                <td className="px-4 py-3 text-xs">{s.email}</td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>
                  {s.billing === "yearly" ? "Jährlich" : "Monatlich"}{s.extra_stores > 0 && ` · +${s.extra_stores} Filialen`}{s.items_tier !== "none" && ` · Artikel ${TIER_LABEL[s.items_tier]}`}
                </td>
                <td className="whitespace-nowrap px-4 py-3">{(s.amount / 100 / (s.billing === "yearly" ? 12 : 1)).toLocaleString("de-CH")}</td>
                <td className="px-4 py-3">
                  <span className="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium"
                    style={s.subscription_status === "active" || s.subscription_status === "trialing"
                      ? { background: "var(--rose-soft)", color: "var(--camel)" }
                      : { background: "var(--surface-secondary)", color: "var(--muted-warm)" }}>
                    {s.cancel_at_period_end ? "Gekündigt z. Ende" : STATUS_LABEL[s.subscription_status] || STATUS_LABEL[s.payment_status] || s.payment_status}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(s.current_period_end)}</td>
                <td className="px-4 py-3">
                  {s.invoice_url ? <a href={s.invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium" style={{ color: "var(--camel)" }} data-testid={`billing-invoice-${s.session_id}`}>Ansehen</a> : "–"}
                </td>
                <td className="px-4 py-3">
                  {s.stripe_subscription_id && !s.cancel_at_period_end && (
                    <button data-testid={`billing-cancel-${s.session_id}`} onClick={() => cancelSub(s.stripe_subscription_id)} title="Zum Periodenende kündigen"
                      className="flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:border-[var(--rose)] hover:text-[var(--rose)]"
                      style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}>
                      <Ban size={14} />
                    </button>
                  )}
                  {!s.stripe_subscription_id && (
                    <button data-testid={`billing-delete-${s.session_id}`} onClick={() => deleteRow(s.session_id)} title="Eintrag löschen"
                      className="flex h-8 w-8 items-center justify-center rounded-full border transition-colors hover:border-[var(--rose)] hover:text-[var(--rose)]"
                      style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs" style={{ color: "var(--muted-warm)" }}>
        Rechnungen & Quittungen erstellt Stripe automatisch pro Abbuchung. Testkarte (Testmodus): 4242 4242 4242 4242, beliebiges Datum/CVC.
      </p>
    </div>
  );
};
